import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BsPersonFill, // Fixed: Swapped to universal user icon
  BsEnvelopeFill,
  BsChatLeftText, // Fixed: Swapped to absolute safe string icon
  BsStarFill,
  BsSendFill,
  BsCheckCircleFill,
  BsExclamationOctagonFill,
  BsShieldCheck,
} from "react-icons/bs";

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    feedback: "",
    rating: 0,
  });

  const [hoverRating, setHoverRating] = useState(0);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Quality Control Desk & Consumer Telemetry | NGO Waters";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRatingSelect = (selectedRating) => {
    setFormData({ ...formData, rating: selectedRating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) {
      setStatus({
        type: "error",
        message: "Please register your evaluation rating before submitting.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      const targetBase =
        import.meta.env?.VITE_API_URL || "http://localhost:5000";
      const response = await axios.post(`${targetBase}/api/feedback`, formData);

      setStatus({
        type: "success",
        message:
          response.data?.message ||
          "Data telemetry submitted successfully to our compliance desk.",
      });
      setFormData({ name: "", email: "", feedback: "", rating: 0 });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          "Operational pipeline error. Please check database latency or try later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };
  const microSpring = { type: "spring", stiffness: 300, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 d-flex align-items-center justify-content-center bg-light py-5 px-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "500px",
          height: "500px",
          background: "rgba(54, 191, 216, 0.02)",
          top: "-100px",
          left: "-50px",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(26, 60, 107, 0.03)",
          bottom: "-80px",
          right: "-50px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-6">
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              transition={fluidSpring}
              className="card border-0 bg-white shadow-sm overflow-hidden"
              style={{ borderRadius: "24px" }}
            >
              <div
                className="p-4 text-center border-bottom border-light"
                style={{ backgroundColor: "rgba(26, 60, 107, 0.015)" }}
              >
                <span
                  className="badge px-2.5 py-1.5 mb-2 text-uppercase fw-semibold"
                  style={{
                    backgroundColor: "rgba(26, 60, 107, 0.06)",
                    color: "var(--brand-deep-blue)",
                    fontSize: "0.68rem",
                  }}
                >
                  Institutional Quality Control
                </span>
                <h4
                  className="fw-bold m-0"
                  style={{
                    color: "var(--brand-deep-blue)",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Ecosystem Telemetry Log
                </h4>
                <p className="text-muted small mb-0 mt-1">
                  Your direct telemetry inputs feed directly into the physical
                  audit systems of Ponia Food And Beverage Pvt. Ltd.
                </p>
              </div>

              <div className="card-body p-4 p-sm-5">
                <AnimatePresence mode="wait">
                  {status.message && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`alert d-flex align-items-center gap-2 small border-0 py-3 mb-4`}
                      style={{
                        backgroundColor:
                          status.type === "success"
                            ? "rgba(40, 167, 69, 0.06)"
                            : "rgba(220, 53, 69, 0.06)",
                        color:
                          status.type === "success" ? "#1e7e34" : "#bd2130",
                        borderRadius: "12px",
                      }}
                    >
                      {status.type === "success" ? (
                        <BsCheckCircleFill className="fs-5 flex-shrink-0" />
                      ) : (
                        <BsExclamationOctagonFill className="fs-5 flex-shrink-0" />
                      )}
                      <span>{status.message}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="form-label text-dark fw-semibold small d-flex align-items-center gap-2"
                    >
                      <BsPersonFill className="text-secondary" /> Full Corporate
                      Name / Client Handle
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-control px-3 py-2.5 bg-light border-0"
                      style={{ borderRadius: "10px", fontSize: "0.9rem" }}
                      placeholder="Identify your operating handle"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="form-label text-dark fw-semibold small d-flex align-items-center gap-2"
                    >
                      <BsEnvelopeFill className="text-secondary" /> Secure
                      Correspondence Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-control px-3 py-2.5 bg-light border-0"
                      style={{ borderRadius: "10px", fontSize: "0.9rem" }}
                      placeholder="operator@domain.com"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="feedback"
                      className="form-label text-dark fw-semibold small d-flex align-items-center gap-2"
                    >
                      <BsChatLeftText
                        className="text-secondary"
                        style={{ fontSize: "0.85rem" }}
                      />{" "}
                      Technical Logs / Experience Summary
                    </label>
                    <textarea
                      id="feedback"
                      name="feedback"
                      value={formData.feedback}
                      onChange={handleChange}
                      className="form-control px-3 py-2.5 bg-light border-0"
                      style={{ borderRadius: "10px", fontSize: "0.9rem" }}
                      rows="4"
                      placeholder="Detail your chemical profile, supply chain, or service parameter feedback..."
                      required
                    ></textarea>
                  </div>

                  <div
                    className="mb-4 text-center bg-light p-3"
                    style={{ borderRadius: "14px" }}
                  >
                    <label className="form-label text-dark fw-bold small d-block mb-2">
                      Ecosystem Rating Assessment
                    </label>
                    <div className="d-flex align-items-center justify-content-center gap-2 py-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <motion.button
                          key={star}
                          type="button"
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.9 }}
                          transition={microSpring}
                          onClick={() => handleRatingSelect(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="btn border-0 p-1 bg-transparent d-inline-flex align-items-center justify-content-center"
                        >
                          <BsStarFill
                            className="fs-3"
                            style={{
                              color:
                                star <= (hoverRating || formData.rating)
                                  ? "#ffc107"
                                  : "rgba(0,0,0,0.12)",
                              transition: "color 0.15s ease",
                            }}
                          />
                        </motion.button>
                      ))}
                    </div>
                    {formData.rating > 0 && (
                      <span
                        className="text-muted d-block mt-2 fw-semibold"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Index Selected: {formData.rating} / 5
                      </span>
                    )}
                  </div>

                  <div className="d-grid mt-4">
                    <motion.button
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      transition={microSpring}
                      type="submit"
                      disabled={isSubmitting}
                      className="btn text-white py-2.5 d-flex align-items-center justify-content-center gap-2 fw-semibold border-0"
                      style={{
                        backgroundColor: "var(--brand-deep-blue, #1a3c6b)",
                        borderRadius: "12px",
                        fontSize: "0.9rem",
                        opacity: isSubmitting ? 0.8 : 1,
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          <span>Compiling Telemetry Blocks...</span>
                        </>
                      ) : (
                        <>
                          <BsSendFill style={{ fontSize: "0.85rem" }} />{" "}
                          Dispatch Validation Record
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>
              </div>

              <div
                className="card-footer bg-transparent py-3 text-center border-top border-light d-flex align-items-center justify-content-center gap-1.5 text-muted"
                style={{ fontSize: "0.7rem" }}
              >
                <BsShieldCheck className="text-success fs-6" />
                <span>
                  Encrypted pipeline to Aligarh Industrial Center Core Ledger.
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FeedbackForm;
